import { concatClasses, safeCss } from '@frontend/web-utils';
import type { ButtonProps as MButtonProps } from '@mantine/core';
import { Button as MButton } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { Icon } from '../Icon/Icon';
import { BUTTON_SIZES } from './Button.constants';
import type { ButtonProps, ButtonSize, ButtonVariant } from './Button.types';
import { getIconColor, getLoaderColor, getVariantClasses } from './Button.variants';

export type { ButtonProps, ButtonSize, ButtonVariant };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      accessibilityLabel,
      className,
      disabled = false,
      fullWidth = false,
      isForm = false,
      isLoading = false,
      leftIconColor,
      leftIconName,
      leftSection,
      onClick,
      rightIconColor,
      rightIconName,
      rightSection,
      size = 'md',
      testId,
      type,
      variant = 'primary',
      ...restProps
    },
    ref,
  ) => {
    const sizeConfig = BUTTON_SIZES[size];
    const variantClasses = getVariantClasses(variant, disabled);
    const iconColor = getIconColor(variant, disabled);
    const loaderColor = getLoaderColor(variant);

    const hasLabel = 'label' in restProps ? !!restProps.label : !!restProps.children;
    const isIconOnly = (leftIconName || rightIconName) && !hasLabel;

    const classNames: ButtonProps['classNames'] = useMemo(
      () => ({
        label: safeCss('font-medium'),
        loader: safeCss('h-auto! leading-none!'),
        root: concatClasses(
          safeCss('content-none click-scale-effect items-center'),
          sizeConfig.height,
          sizeConfig.paddingX,
          sizeConfig.paddingY,
          sizeConfig.fontSize,
          sizeConfig.borderWidth,
          sizeConfig.borderRadius,
          variantClasses,
          fullWidth ? 'w-full' : '',
          className ?? '',
        ),
        section: isIconOnly ? safeCss('![margin-inline-end:0]') : '',
      }),
      [variant, size, disabled, fullWidth, className, sizeConfig, variantClasses, isIconOnly],
    );

    const ariaLabel = 'label' in restProps ? restProps.label : accessibilityLabel;

    return (
      <MButton
        {...restProps}
        aria-label={ariaLabel}
        classNames={classNames}
        data-testid={testId}
        disabled={disabled}
        leftSection={leftIconName ? <Icon color={leftIconColor ?? iconColor} name={leftIconName} /> : leftSection}
        loaderProps={{
          color: loaderColor as NonNullable<MButtonProps['loaderProps']>['color'],
          size: sizeConfig.loaderSize,
        }}
        loading={isLoading}
        onClick={
          isForm
            ? undefined
            : (e) => {
                e.stopPropagation();
                e.preventDefault();
                onClick?.(e);
              }
        }
        ref={ref}
        rightSection={rightIconName ? <Icon color={rightIconColor ?? iconColor} name={rightIconName} /> : rightSection}
        type={type ?? 'submit'}
        variant="filled"
      >
        {'label' in restProps ? restProps.label || null : restProps.children}
      </MButton>
    );
  },
);
